import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { authService } from '../services/authService';
import { apiService } from '../services/apiService';
import { DSIcon, DSInput } from '../design-system/primitives';
import { personName } from '../utils/personName';

function VerifyMember() {
  const { qrCode } = useParams();
  const [memberData, setMemberData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  
  // Add credits modal state
  const [showAddCreditsModal, setShowAddCreditsModal] = useState(false);
  const [creditAmount, setCreditAmount] = useState('');
  const [creditDescription, setCreditDescription] = useState('');
  const [addingCredits, setAddingCredits] = useState(false);
  const [creditSuccess, setCreditSuccess] = useState('');
  const [creditError, setCreditError] = useState('');


  useEffect(() => {
    // Get current logged-in user
    const user = authService.getCurrentUser();
    setCurrentUser(user);
    
    const verifyMember = async () => {
      try {
        setLoading(true);
        const API_URL = process.env.REACT_APP_API_URL || '/api';
        const response = await axios.get(`${API_URL}/public/verify/${qrCode}`);
        setMemberData(response.data);
      } catch (err) {
        if (err.response && err.response.status === 404) {
          setError('Member not found');
        } else {
          setError('Failed to verify member');
        }
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    
    verifyMember();
  }, [qrCode]);

  // Check if current user can add credits (admin or host)
  const canAddCredits = currentUser && (currentUser.role === 'admin' || currentUser.role === 'host');
  // Only admins can see the balance
  const canSeeBalance = currentUser && currentUser.role === 'admin';

  const handleAddCredits = async (e) => {
    e.preventDefault();
    setCreditError('');
    setCreditSuccess('');
    
    const amount = parseFloat(creditAmount);
    if (isNaN(amount) || amount <= 0) {
      setCreditError('Please enter a valid positive amount');
      return;
    }

    setAddingCredits(true);
    try {
      await apiService.addCredits({
        userId: memberData.userId,
        amount: amount,
        description: creditDescription || 'Credits added via QR scan'
      });
      
      setCreditSuccess(`Successfully added ${amount} credits!`);
      setCreditAmount('');
      setCreditDescription('');
      
      // Update the displayed credit balance
      setMemberData(prev => ({
        ...prev,
        creditBalance: (prev.creditBalance || 0) + amount
      }));
      
      // Close modal after short delay
      setTimeout(() => {
        setShowAddCreditsModal(false);
        setCreditSuccess('');
      }, 2000);
    } catch (err) {
      console.error('Error adding credits:', err);
      setCreditError(err.response?.data?.error || 'Failed to add credits');
    } finally {
      setAddingCredits(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.loading}>Verifying...</div>
        </div>
      </div>
    );
  }

  if (error || !memberData || !memberData.valid) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.iconContainer}>
            <div style={styles.errorIcon}><DSIcon name="xCircle" /></div>
          </div>
          <h1 style={styles.title}>Member Not Found</h1>
          <p style={styles.message}>
            This QR code is not associated with a valid member.
          </p>
        </div>
      </div>
    );
  }

  const displayName = personName(memberData) || 'Member';

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.iconContainer}>
          {memberData.isMember ? (
            <div style={styles.successIcon}><DSIcon name="check" /></div>
          ) : (
            <div style={styles.warningIcon}><DSIcon name="warning" /></div>
          )}
        </div>
        
        <h1 style={styles.greeting}>
          Hi, I'm {displayName}!
          {memberData.pronouns && (
            <div style={{ fontSize: '0.6em', fontWeight: 'normal', marginTop: '8px', color: 'var(--ds-color-textMuted)' }}>
              ({memberData.pronouns})
            </div>
          )}
        </h1>
        
        <div style={styles.statusBadge}>
          {memberData.isMember ? (
            <div style={styles.activeMemberBadge}>
              <DSIcon name="check" /> Active Member
            </div>
          ) : (
            <div style={styles.inactiveMemberBadge}>
              Not Currently a Member
            </div>
          )}
        </div>

        {/* Credit Balance - shown only to admins */}
        {canSeeBalance && (
          <div style={styles.creditBalanceSection}>
            <p style={styles.creditBalanceLabel}>Credit Balance</p>
            <p style={styles.creditBalanceValue}>{parseFloat(memberData.creditBalance || 0).toFixed(2)}</p>
          </div>
        )}

        {/* Today's Events - shown to all users */}
        {memberData.todayEvents && memberData.todayEvents.length > 0 && (
          <div style={styles.todayEventsSection}>
            <h3 style={styles.todayEventsTitle}><DSIcon name="calendar" /> Today's Events</h3>
            {memberData.todayEvents.map((event) => (
              <div key={event.id} style={styles.eventCard}>
                <div style={styles.eventHeader}>
                  <span style={styles.eventTitle}>{event.title}</span>
                  {event.checkedIn ? (
                    <span style={styles.checkedInBadge}><DSIcon name="check" /> Checked In</span>
                  ) : (
                    <span style={styles.rsvpBadge}>RSVP: {event.status}</span>
                  )}
                </div>
                {event.checkedIn && event.checkedInAt && (
                  <div style={styles.eventCheckInTime}>
                    Checked in at {new Date(event.checkedInAt).toLocaleTimeString('en-US', {
                      hour: 'numeric',
                      minute: '2-digit'
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Guest Pass Information - Future Feature */}
        {!memberData.isMember && memberData.lastGuestPass && (
          <div style={styles.guestPassInfo}>
            <h3 style={styles.guestPassTitle}><DSIcon name="ticket" /> Guest Pass</h3>
            <p style={styles.guestPassText}>
              Last purchased: {new Date(memberData.lastGuestPass).toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit'
              })}
            </p>
          </div>
        )}

        {/* Action Buttons - Only visible to hosts and admins */}
        {canAddCredits && (
          <div style={styles.actionButtons}>
            <button 
              style={styles.addCreditsButton}
              onClick={() => setShowAddCreditsModal(true)}
            >
              <DSIcon name="dollar" /> Add Credits
            </button>
          </div>
        )}

        <div style={styles.footer}>
          <img 
            src="/logo.png" 
            alt="Capital Pinball" 
            style={styles.logo}
          />
          <p style={styles.footerText}>Capital Pinball Membership</p>
        </div>
      </div>

      {/* Add Credits Modal */}
      {showAddCreditsModal && (
        <div style={styles.modalOverlay} onClick={() => setShowAddCreditsModal(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>Add Credits for {displayName}</h2>
            
            <form onSubmit={handleAddCredits}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Amount</label>
                <DSInput
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={creditAmount}
                  onChange={(e) => setCreditAmount(e.target.value)}
                  placeholder="Enter amount"
                  style={styles.input}
                  required
                  disabled={addingCredits}
                />
              </div>
              
              <div style={styles.formGroup}>
                <label style={styles.label}>Description (optional)</label>
                <DSInput
                  type="text"
                  value={creditDescription}
                  onChange={(e) => setCreditDescription(e.target.value)}
                  placeholder="e.g., Cash payment received"
                  style={styles.input}
                  disabled={addingCredits}
                />
              </div>

              {creditError && (
                <div style={styles.errorMessage}>{creditError}</div>
              )}
              
              {creditSuccess && (
                <div style={styles.successMessage}>{creditSuccess}</div>
              )}

              <div style={styles.modalButtons}>
                <button 
                  type="button" 
                  style={styles.cancelButton}
                  onClick={() => setShowAddCreditsModal(false)}
                  disabled={addingCredits}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  style={styles.submitButton}
                  disabled={addingCredits}
                >
                  {addingCredits ? 'Adding...' : 'Add Credits'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
    padding: '20px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
  },
  card: {
    backgroundColor: 'white',
    borderRadius: '16px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
    padding: '40px',
    maxWidth: '500px',
    width: '100%',
    textAlign: 'center'
  },
  loading: {
    fontSize: '24px',
    color: '#666',
    padding: '40px'
  },
  iconContainer: {
    marginBottom: '20px'
  },
  successIcon: {
    fontSize: '80px',
    color: '#28a745',
    fontWeight: 'bold',
    display: 'inline-block',
    width: '100px',
    height: '100px',
    lineHeight: '100px',
    backgroundColor: '#d4edda',
    borderRadius: '50%'
  },
  errorIcon: {
    fontSize: '60px',
    display: 'inline-block',
    width: '100px',
    height: '100px',
    lineHeight: '100px',
    backgroundColor: '#f8d7da',
    borderRadius: '50%'
  },
  warningIcon: {
    fontSize: '60px',
    display: 'inline-block',
    width: '100px',
    height: '100px',
    lineHeight: '100px',
    backgroundColor: '#fff3cd',
    borderRadius: '50%',
    color: '#856404'
  },
  title: {
    fontSize: '32px',
    color: '#333',
    marginBottom: '10px',
    fontWeight: '600'
  },
  greeting: {
    fontSize: '32px',
    color: '#333',
    marginBottom: '20px',
    fontWeight: '600'
  },
  message: {
    fontSize: '18px',
    color: '#666',
    lineHeight: '1.6'
  },
  statusBadge: {
    marginTop: '20px',
    marginBottom: '30px'
  },
  activeMemberBadge: {
    display: 'inline-block',
    padding: '12px 24px',
    backgroundColor: '#28a745',
    color: 'white',
    borderRadius: '24px',
    fontSize: '18px',
    fontWeight: '600'
  },
  inactiveMemberBadge: {
    display: 'inline-block',
    padding: '12px 24px',
    backgroundColor: '#ffc107',
    color: '#212529',
    borderRadius: '24px',
    fontSize: '18px',
    fontWeight: '600'
  },
  creditBalanceSection: {
    marginTop: '20px',
    padding: '15px 20px',
    backgroundColor: '#e8f5e9',
    borderRadius: '8px',
    border: '1px solid #c8e6c9'
  },
  creditBalanceLabel: {
    fontSize: '14px',
    color: '#666',
    margin: '0 0 5px 0'
  },
  creditBalanceValue: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#2e7d32',
    margin: '0'
  },
  todayEventsSection: {
    marginTop: '20px',
    padding: '15px 20px',
    backgroundColor: '#e3f2fd',
    borderRadius: '8px',
    border: '1px solid #90caf9'
  },
  todayEventsTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#1976d2',
    margin: '0 0 12px 0',
    textAlign: 'left'
  },
  eventCard: {
    backgroundColor: 'white',
    padding: '12px',
    borderRadius: '6px',
    marginBottom: '8px',
    border: '1px solid #bbdefb'
  },
  eventHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '10px',
    flexWrap: 'wrap'
  },
  eventTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#333',
    textAlign: 'left',
    flex: 1
  },
  checkedInBadge: {
    display: 'inline-block',
    padding: '4px 10px',
    backgroundColor: '#28a745',
    color: 'white',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
    whiteSpace: 'nowrap'
  },
  rsvpBadge: {
    display: 'inline-block',
    padding: '4px 10px',
    backgroundColor: '#007bff',
    color: 'white',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
    textTransform: 'capitalize',
    whiteSpace: 'nowrap'
  },
  eventCheckInTime: {
    fontSize: '12px',
    color: '#666',
    marginTop: '6px',
    textAlign: 'left'
  },
  guestPassInfo: {
    marginTop: '20px',
    padding: '15px 20px',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    border: '1px solid #dee2e6'
  },
  guestPassTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#333',
    margin: '0 0 8px 0'
  },
  guestPassText: {
    fontSize: '14px',
    color: '#666',
    margin: '0'
  },
  actionButtons: {
    marginTop: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  addCreditsButton: {
    padding: '14px 28px',
    backgroundColor: '#007bff',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background-color 0.2s'
  },
  footer: {
    marginTop: '40px',
    paddingTop: '30px',
    borderTop: '1px solid #e9ecef'
  },
  logo: {
    width: '60px',
    height: '60px',
    marginBottom: '10px'
  },
  footerText: {
    fontSize: '14px',
    color: '#999',
    margin: '0'
  },
  // Modal styles
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '30px',
    maxWidth: '400px',
    width: '90%',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
  },
  modalTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#333',
    marginBottom: '20px',
    textAlign: 'center'
  },
  formGroup: {
    marginBottom: '16px'
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '500',
    color: '#333',
    marginBottom: '6px'
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '16px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    boxSizing: 'border-box'
  },
  modalButtons: {
    display: 'flex',
    gap: '12px',
    marginTop: '24px'
  },
  cancelButton: {
    flex: 1,
    padding: '12px',
    backgroundColor: '#f8f9fa',
    color: '#333',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '16px',
    cursor: 'pointer'
  },
  submitButton: {
    flex: 1,
    padding: '12px',
    backgroundColor: '#28a745',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  errorMessage: {
    backgroundColor: '#f8d7da',
    color: '#721c24',
    padding: '10px',
    borderRadius: '6px',
    marginBottom: '10px',
    fontSize: '14px'
  },
  successMessage: {
    backgroundColor: '#d4edda',
    color: '#155724',
    padding: '10px',
    borderRadius: '6px',
    marginBottom: '10px',
    fontSize: '14px'
  }
};

export default VerifyMember;
